import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { TripChatExportFormat } from 'tripPlanner/api/v3'
import { useCreateTripChatExportMutation } from 'tripPlanner/hooks/api/chat'

interface UseTripChatItineraryExportArgs {
  chatId: number | undefined
  onExportCreated?: () => void
}

export function useTripChatItineraryExport({
  chatId,
  onExportCreated,
}: UseTripChatItineraryExportArgs) {
  const intl = useIntl()
  const exportInFlightRef = useRef(false)
  const [loadingFormat, setLoadingFormat] = useState<TripChatExportFormat | undefined>(undefined)
  const { mutateAsync: createExport, isLoading } = useCreateTripChatExportMutation()

  const errorMessage = intl.formatMessage({
    id: 'h5ZSfq2gP8m3F-9GURmi',
    defaultMessage: 'Unable to download itinerary. Please try again.',
    description: 'Snackbar error message when trip planner itinerary export creation fails',
  })

  const downloadItinerary = useCallback(async(format: TripChatExportFormat) => {
    if (!chatId || isLoading || exportInFlightRef.current) return
    exportInFlightRef.current = true
    setLoadingFormat(format)

    try {
      const result = await createExport({ chatId, format })
      onExportCreated?.()
      window.location.assign(result.downloadUrl)
      return result.downloadUrl
    } catch(error) {
      console.error('[TripChat] Failed to create itinerary export', error)
      showSnackbar(errorMessage, 'critical')
    } finally {
      exportInFlightRef.current = false
      setLoadingFormat(undefined)
    }
  }, [chatId, createExport, errorMessage, isLoading, onExportCreated])

  const downloadPdf = useCallback(() => {
    return downloadItinerary('pdf')
  }, [downloadItinerary])

  const downloadSpreadsheet = useCallback(() => {
    return downloadItinerary('xlsx')
  }, [downloadItinerary])

  return {
    downloadPdf,
    downloadSpreadsheet,
    isExportLoading: isLoading,
    loadingFormat,
  }
}
